import { useState } from "react";
import { Box, Flex, Text, Button, Stack, HStack, Input } from "@chakra-ui/react";
import { ShieldPlus, Copy } from "lucide-react";
import { apiPost } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { colors } from "@/theme/colors";

// Platform-level: invite ANOTHER Hakina Super-Admin (company_id NULL).
// Copy-link first — works without email delivery.
export function SuperAdminInvite({ flash }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ email: "", full_name: "" });
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);
  const [err, setErr] = useState(null);

  const submit = async () => {
    setErr(null);
    if (!f.email.trim()) { setErr("Email is required."); return; }
    setBusy(true);
    try {
      const res = await apiPost("/platform/super-admins/invite", {
        email: f.email.trim(), full_name: f.full_name.trim() || null,
      });
      setLink(`${window.location.origin}/accept-invite?token_hash=${res.hashed_token}&type=recovery`);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(link);
    flash("success", "Invite link copied — valid for ~1 hour, single use.");
  };

  const close = () => { setOpen(false); setLink(null); setF({ email: "", full_name: "" }); setErr(null); };

  return (
    <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="14px" p="5" data-testid="super-admin-invite-card">
      <Flex justify="space-between" align="center" gap="3" flexWrap="wrap">
        <Box>
          <Text fontWeight="700" fontSize="sm" color={colors.textPrimary}>Hakina team — Platform Super-Admins</Text>
          <Text fontSize="xs" color={colors.textSecondary}>Full platform access across all companies. Invitation-only.</Text>
        </Box>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="invite-super-admin-btn">
          <ShieldPlus size={15} /> Invite Super-Admin
        </Button>
      </Flex>

      {open && (
        <Modal open onClose={close} title="Invite a Platform Super-Admin" testId="super-invite-modal">
          <Stack gap="3">
            {err && <Box px="3" py="2" borderRadius="8px" bg={colors.criticalBg} color={colors.criticalText} fontSize="sm" data-testid="super-invite-error">{err}</Box>}
            {link ? (
              <>
                <Text fontSize="sm" color={colors.textPrimary}>
                  Send this set-password link to <b>{f.email}</b>. It's <b>single-use</b> and expires in about an hour — re-invite to mint a fresh one.
                </Text>
                <HStack gap="2">
                  <Input value={link} readOnly fontSize="xs" bg={colors.cream} data-testid="super-invite-link" />
                  <Button size="sm" onClick={copy} data-testid="super-invite-copy-btn"><Copy size={14} /> Copy</Button>
                </HStack>
                <Button size="sm" variant="outline" onClick={close} data-testid="super-invite-done-btn">Done</Button>
              </>
            ) : (
              <>
                <Box>
                  <Text fontSize="sm" fontWeight="500" mb="1.5" color={colors.textPrimary}>Email</Text>
                  <Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="name@hakina.ca" data-testid="super-invite-email" />
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="500" mb="1.5" color={colors.textPrimary}>Full name (optional)</Text>
                  <Input value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} data-testid="super-invite-name" />
                </Box>
                <Text fontSize="xs" color={colors.textSecondary}>
                  They get FULL platform access: every company, master templates, global resources. You'll receive a copyable set-password link.
                </Text>
                <Button onClick={submit} loading={busy} data-testid="super-invite-submit">Create invite link</Button>
              </>
            )}
          </Stack>
        </Modal>
      )}
    </Box>
  );
}
